import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';
import { AppContext } from 'src/app/app/context';
import { VisualDto } from 'src/app/services/api.services';
import { LocalisationService } from 'src/app/services/localisation.service';
import { getInsightsPbiChartType } from 'src/app/shared/chart/chart';
import { AppSelectors } from 'src/app/state/app';
import { FilterUtils } from '../utils/FilterUtils';
import { Visual } from '../visuals/Visual';

@Component({
  selector: 'visual-embed-details',
  templateUrl: './VisualEmbedDetails.component.html',
  styleUrl: './VisualEmbedDetails.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VisualEmbedDetailsComponent {
  readonly appContext = inject(AppContext);
  readonly localizationService = inject(LocalisationService);
  readonly store = inject(Store);
  readonly itemCategories = toSignal(this.store.pipe(select(AppSelectors.getItemCategories)));

  readonly loading = input<boolean>(true);

  readonly visualDto = input<VisualDto>(null);
  readonly visual = input<Visual>(null);

  readonly sections = computed<ReadonlyArray<SectionModel>>(() => {
    if (this.loading())
      return [];

    let dto = this.visualDto();
    let visual = this.visual();

    if (!dto || !visual)
      return [];

    let filterFormatter = new FilterUtils.Formatter(
      visual.context.visualFieldTargetResolver(),
      visual.context.dataFormatter());

    let sections: SectionModel[] = [
      {
        title: 'Name',
        items: [{ title: dto.name }],
      }
    ];

    if (dto.content?.subtitle) {
      sections.push({
        title: 'Subtitle',
        items: [{ title: dto.content?.subtitle }]
      });
    }

    sections.push(...[
      {
        title: 'Type',
        items: [{ title: getInsightsPbiChartType(dto.content.type).title }],
      },
      {
        title: 'Dataset',
        items: [{ title: visual.context.schema.title }],
      },
      {
        title: 'Fields',
        items: [...visual.fields.all()].map(f => ({
          title: f.isAggregated 
            ? f.measureTitle
            : (f.target.isChild ? `${f.target.parent.shortTitle} - ${f.shortTitle}` : f.shortTitle),
          tooltip: f.longTitle,
          invalid: !f.isValid()
        })),
        nowrap: true,
      },
      {
        title: 'Filters',
        items: [...visual.filters()].map((f, i) => ({
          title:   filterFormatter.formatDescription(f.createFilterDto(), { capitalizeFirstWord: i == 0 }),
          tooltip: filterFormatter.formatDescription(f.createFilterDto(), { capitalizeFirstWord: i == 0, noHtml: true }),
          invalid: !f.isValid()
        })),
        nowrap: true,
      },
      {
        title: 'Description',
        items: [{ title: dto.description }],
      },
      {
        title: 'Shared',
        items: [{ title: dto.shared ? 'Yes' : 'No' }],
      },
    ]);

    if (dto.fromLibrary) {
      let allCategories = this.itemCategories();
      let categories = dto.categoryIds?.map(id => allCategories.find(x => x.id == id));
      let categoryNames = categories.map(x => x.name);

      sections.push(
        {
          title: 'Categories',
          items: categoryNames.map(x => ({ title: x }))
        });
    }
    else {
      let medium = this.localizationService.CUSTOM_DATE_FORMATS.mediumDateTimeNoSeconds;

      let datePipe = new DatePipe(this.appContext.localeId());

      sections.push(
        {
          title: 'Modified',
          items: [{ title: `${dto.updatedBy} - ${datePipe.transform(dto.updatedDate, medium)}` }],
        },
        {
          title: 'Owner',
          items: [{ title: `${dto.createdBy} - ${datePipe.transform(dto.createdDate, 'short')}` }],
        });
    }

    return sections;
  });
}

interface SectionModel {
  readonly title: string;
  readonly items: ReadonlyArray<SectionItemModel>;
  readonly nowrap?: boolean;
}

interface SectionItemModel {
  readonly title: string;
  readonly tooltip?: string;
  readonly invalid?: boolean;
  readonly nowrap?: boolean;
}
